import { useCallback } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';

import { Button, ButtonSize, ButtonType } from '@xemplo/button';
import { DawnCircleCross } from '@xemplo/icons';
import { Colour } from '@xemplo/style-constants';

import { defaultText } from './page-not-found.const';
import * as S from './page-not-found.styles';
import { PageNotFoundProps } from './page-not-found.types';

const isAbsoluteUrl = (url: string) =>
  url.startsWith('http://') || url.startsWith('https://');

/**
 * Can be rendered with or without a react-router context. It is used as
 * the ErrorBoundary fallback, so it must never assume a Router is present
 * (e.g. when the boundary wraps the app above the RouterProvider).
 */
export function PageNotFound(props: PageNotFoundProps) {
  const inRouter = useInRouterContext();

  if (inRouter) return <PageNotFoundWithRouter {...props} />;

  return (
    <PageNotFoundContent
      {...props}
      onNavigateHome={() => window.location.assign(props.homeUrl ?? '/')}
    />
  );
}

function PageNotFoundWithRouter(props: PageNotFoundProps) {
  const { homeUrl } = props;
  const navigate = useNavigate();

  const handleNavigate = useCallback(() => {
    if (!homeUrl) {
      navigate('/');
      return;
    }

    if (isAbsoluteUrl(homeUrl)) {
      window.location.href = homeUrl;
      return;
    }

    navigate(homeUrl);
  }, [homeUrl, navigate]);

  return <PageNotFoundContent {...props} onNavigateHome={handleNavigate} />;
}

type PageNotFoundContentProps = PageNotFoundProps & {
  onNavigateHome: () => void;
};

function PageNotFoundContent({
  homeBtnText,
  hideHomeBtn = false,
  title,
  subtitle,
  customIcon: CustomIcon = DawnCircleCross,
  onNavigateHome,
}: PageNotFoundContentProps) {
  return (
    <S.Container>
      <S.ContentWrapper>
        <S.Content>
          <CustomIcon width={64} height={64} color={Colour.Gray[200]} />
          <S.Title>{title ?? defaultText.title}</S.Title>
          <S.Subtitle>{subtitle ?? defaultText.subtitle}</S.Subtitle>
        </S.Content>
        {!hideHomeBtn && (
          <Button
            ariaLabel={homeBtnText ?? defaultText.homeButton}
            type={ButtonType.Primary}
            size={ButtonSize.Medium}
            onClick={onNavigateHome}
          >
            {homeBtnText ?? defaultText.homeButton}
          </Button>
        )}
      </S.ContentWrapper>
    </S.Container>
  );
}
